import { AccountBalance, Cable, Dns, GridView, Looks4Outlined, Looks6Outlined, SolarPower, Undo, WbSunny } from '@mui/icons-material'
import { Box, TextField, Typography } from '@mui/material'
import clone from 'clone'
import { Trans } from 'react-i18next'
import { Dropdown } from '../../components/atoms/Dropdown'
import { ListSelector } from '../../components/atoms/ListSelector'
import { Hostname, Port } from '../../types/models'
import { PortConfigSolar, SolarConfig, SolarPort } from '../../types/ports'
import { baudrates, SolarPortConfigParams, SolarPortDriver, SolarPortSolisConfigParams, SolarPortStecaGridConfigParams, SolarPortSunspecConfigParams } from '../../types/solar'
import { DeviceControl } from '../DeviceControl'
import { PortControl } from './PortControl'
import { HelpTitle } from '../../components/atoms/HelpTitle'
import { CustomSwitch } from '../../components/atoms/CustomSwitch'

/**
 * Get the default config for driver.
 */
export function driverDefaults(driver: SolarPortDriver): SolarPortConfigParams {
  switch (driver) {
    case 'SPP_SOLAR_DRIVER_SUNSPEC':
      return {
        conn_type: 'SOLAR_CONN_TYPE_MODBUS_TCP_HOSTNAME',
        conn_params: '' as Hostname,
        slave_id: 0,
        extras: [
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
          [0, 0],
        ]
      }

    case 'SPP_SOLAR_DRIVER_STECAGRID':
      return {
        rs485_baudrate: 'AUTO',
        rs485_mode: '8N1',
        rs485_instance: 0,
        our_id: 0,
        slave_id: 0,
        auth_level: 'CUSTOMER',
      }

    case 'SPP_SOLAR_DRIVER_SOLIS':
    case 'SPP_SOLAR_DRIVER_SOFAR_G3':
    case 'SPP_SOLAR_DRIVER_SOFAR_G2':
      return {
        conn_type: 'SOLAR_CONN_TYPE_MODBUS_RTU_RS485_0',
        slave_id: 1,
        extras: [
          {
            func_code: 0,
            reg_addr: 0x0000,
          },
          {
            func_code: 0,
            reg_addr: 0x0000,
          },
          {
            func_code: 0,
            reg_addr: 0x0000,
          },
          {
            func_code: 0,
            reg_addr: 0x0000,
          },
          {
            func_code: 0,
            reg_addr: 0x0000,
          },
        ]
      }

    case 'SPP_SOLAR_DRIVER_RELAYED':
      return {
      }

    default:
      throw new Error(`Not implemented defaults for driver '${driver}'.`)
  }
}

/**
 * Controller for solar panel.
 */
export class SolarPortControl extends PortControl<SolarPort, SolarConfig> {

  port: SolarPort

  constructor(deviceControl: DeviceControl, port: Port) {
    super(deviceControl, port)
    this.port = port as SolarPort
  }

  getDescription(): string { return this.app.t('Solar power system') }

  getTypeName(): string { return this.app.t('PortType-Photovoltaic') }

  get group(): string { return this.app.t('PortGroup-Energy Production') }

  getName(): string {
    return this.app.t('PortName-Solar {number}').replace('{number}', `${this.number}`)
  }

  getConfigDefaults(): PortConfigSolar {
    return {
      enabled: false,
      to_summer: false,
      driver: 'SPP_SOLAR_DRIVER_SUNSPEC',
      params: driverDefaults('SPP_SOLAR_DRIVER_SUNSPEC')
    } as PortConfigSolar
  }

  configBottomLeft() : React.ReactNode | undefined {

    const driver: string = this.getConfig('driver')
    const params: Record<string, any> = this.getConfig('params')
    const advanced: boolean = true // TODO: Handle differently.

    const onChangeDriver = (driver: string) => {

      this.setConfig('enabled', true)
      this.setConfig('driver', driver)

      const newParams = driverDefaults(driver as SolarPortDriver)

      this.setConfig('params', newParams)
    }

    const onChangeParam = (param: string, value: any) => {
      const newParams: Record<string, any> = this.getConfig('params') ? clone(this.getConfig('params')) : {}
      newParams[param] = value

      // Ensure `enabled` in pending.
      this.setConfig('enabled', true)

      this.setConfig('params', newParams)
    }

    return (
      <Box>
        <Trans>Inverter Model</Trans>

        <ListSelector
          items={{
            SPP_SOLAR_DRIVER_SUNSPEC: 'SunSpec',
            SPP_SOLAR_DRIVER_STECAGRID: 'StecaGrid',
            SPP_SOLAR_DRIVER_SOLIS: 'Solis',
            SPP_SOLAR_DRIVER_SOFAR_G3: 'Sofar G3',
            SPP_SOLAR_DRIVER_SOFAR_G2: 'Sofar G2',
            SPP_SOLAR_DRIVER_RELAYED: this.app.t('Relayed Data'),
          }}
          icons={[WbSunny, GridView, SolarPower, AccountBalance, AccountBalance, Undo]}
          selected={driver || null}
          onSelect={onChangeDriver}
        />

        {
          driver === 'SPP_SOLAR_DRIVER_SOLIS' &&
          <Box sx={{ mt: 1, color: 'error.main' }}>
            <Trans>This means PV-only inverter. If you want PV data from a battery hybrid inverter, create Battery appliance (or, if already done, choose "Relayed data").</Trans>
          </Box>
        }

        {
          advanced && driver !== 'SPP_SOLAR_DRIVER_RELAYED' &&
          <TextField
            margin="normal"
            type="number"
            sx={{ width: '51%' }}
            data-cy='text-Slave ID Solar Inverter'
            label={this.app.t('Slave ID')}
            size="small"
            disabled={!driver}
            value={params ? `${params.slave_id}` : ''}
            onChange={(e) => onChangeParam('slave_id', parseInt(e.target.value || '0'))}
          />
        }

        {
          advanced && driver === 'SPP_SOLAR_DRIVER_STECAGRID' &&
          <TextField
            margin="normal"
            type="number"
            sx={{ width: '51%' }}
            label={this.app.t('Our ID')}
            size="small"
            value={params ? `${(params as SolarPortStecaGridConfigParams).our_id}` : ''}
            onChange={(e) => onChangeParam('our_id', parseInt(e.target.value || '0'))}
          />
        }
      </Box>
    )
  }

  configBottomRight() : React.ReactNode | undefined {

    const driver = this.getConfig('driver')
    const params = this.getConfig('params')
    const toSummer = this.getConfig('to_summer')
    const advanced: boolean = true // TODO: Handle differently.

    if (!advanced) {
      return <></>
    }

    const onChangeParam = (param: string, value: any) => {
      const newParams: Record<string, any> = this.getConfig('params') ? clone(this.getConfig('params')) : {}
      if (param === 'ip') {
        param = 'conn_params'
        value = value + ':' + ((newParams.conn_params || '').split(':')[1] || '')
      }
      if (param === 'port') {
        param = 'conn_params'
        value = ((newParams.conn_params || '').split(':')[0] || '') + ':' + value
      }
      newParams[param] = value
      if (param === 'conn_type') {
        const rs485 = value === 'SOLAR_CONN_TYPE_MODBUS_RTU_RS485_0' || value === 'SOLAR_CONN_TYPE_MODBUS_RTU_RS485_1'

        if (driver !== 'SPP_SOLAR_DRIVER_SOLIS' && driver !== 'SPP_SOLAR_DRIVER_SOFAR_G3' && driver !== 'SPP_SOLAR_DRIVER_SOFAR_G2') {
          newParams.conn_params = rs485 ? { baudrate: 'AUTO', parity: 'ODD' } : ''
        }
      }

      // Ensure `enabled` in pending.
      this.setConfig('enabled', true)

      this.setConfig('params', newParams)
    }

    // Commonly shared net measuring control.
    const Measuring = <Box sx={{ mb: 2 }}>
      <HelpTitle title={this.app.t('Net Metering')} code="SOLAR"/>

      <Box sx={{ display: 'flex' }}>
        <CustomSwitch data-cy="switch-To Summer" onClick={() => this.setConfig('to_summer', !toSummer)} checked={!!toSummer}/>
          <Typography component="span" sx={{ ml: 2 }}>
            <Trans>Part of building net metering?</Trans>
          </Typography>
      </Box>

      {!!toSummer && <Box sx={{ mt: 1, color: 'error.main' }}>
          <Trans>Are you sure? We recommend metering the whole supply if possible. Use this choice only if the energy meter does not measure the whole supply but loads only for example.</Trans>
        </Box>
      }

    </Box>

    // Define UI per driver.
    if (driver === 'SPP_SOLAR_DRIVER_SUNSPEC' && params) {
      const solarParams: SolarPortSunspecConfigParams = params as SolarPortSunspecConfigParams

      return (
        <Box>
          {Measuring}

          <Trans>Connection</Trans>

          <ListSelector
            items={{
              SOLAR_CONN_TYPE_MODBUS_RTU_RS485_0:
                this.app.t('ConnType-RS485') +
                ' ' + this.app.t('Port {number}').replace('{number}', '0'),
              SOLAR_CONN_TYPE_MODBUS_RTU_RS485_1:
                this.app.t('ConnType-RS485') +
                ' ' + this.app.t('Port {number}').replace('{number}', '1'),
              SOLAR_CONN_TYPE_MODBUS_TCP_IP4ADDR: this.app.t('ConnType-IPV4'),
              // SOLAR_CONN_TYPE_MODBUS_TCP_IP6ADDR: this.app.t('ConnType-IPV6'),
              SOLAR_CONN_TYPE_MODBUS_TCP_HOSTNAME: this.app.t('ConnType-Hostname'),
            }}
            icons={[
              Cable,
              Cable,
              Looks4Outlined,
              Looks6Outlined,
              Dns
            ]}
            selected={solarParams.conn_type}
            onSelect={(e) => onChangeParam('conn_type', e)}
          />

          {
            (solarParams.conn_type === 'SOLAR_CONN_TYPE_MODBUS_RTU_RS485_0' || solarParams.conn_type === 'SOLAR_CONN_TYPE_MODBUS_RTU_RS485_1') &&
              <>
                <Dropdown
                  sx={{ mt: 2 }}
                  label={this.app.t('Baudrate')}
                  items={baudrates}
                  selected={
                    typeof solarParams.conn_params === 'string' ? '' : `${solarParams.conn_params.baudrate}`
                  }
                  onSelect={
                    value => onChangeParam('conn_params', { baudrate: parseInt(value), parity: typeof solarParams.conn_params === 'string' ? 'ODD' : solarParams.conn_params.parity })
                  }
                />
                <Dropdown
                  sx={{ mt: 2 }}
                  label={this.app.t('Parity')}
                  items={{
                    ODD: this.app.t('Parity-ODD'),
                    EVEN: this.app.t('Parity-EVEN'),
                    NO_PARITY: this.app.t('Parity-NO_PARITY'),
                  }}
                  selected={
                    typeof solarParams.conn_params === 'string' ? 'ODD' : `${solarParams.conn_params.parity}`
                  }
                  onSelect={
                    value => onChangeParam('conn_params', { parity: value, baudrate: typeof solarParams.conn_params === 'string' ? 0 : solarParams.conn_params.baudrate })
                  }
                />
              </>
          }

          {
            solarParams.conn_type === 'SOLAR_CONN_TYPE_MODBUS_TCP_HOSTNAME' &&
              <TextField
                margin="normal"
                label={this.app.t('Hostname')}
                size="small"
                value={params ? solarParams.conn_params : ''}
                onChange={(e) => onChangeParam('conn_params', e.target.value)}
              />
          }

          {
            solarParams.conn_type === 'SOLAR_CONN_TYPE_MODBUS_TCP_IP4ADDR' &&
              <>
                <TextField
                  data-cy='text-IP Address Solar Inverter'
                  margin="normal"
                  label={this.app.t('IP Address')}
                  size="small"
                  value={params ? `${solarParams.conn_params}`.split(':')[0] || '' : ''}
                  onChange={(e) => onChangeParam('ip', e.target.value)}
                />
                <TextField
                  data-cy='text-Port Solar Inverter'
                  margin="normal"
                  sx={{ width: '50%' }}
                  label={this.app.t('Port')}
                  size="small"
                  value={params ? `${solarParams.conn_params}`.split(':')[1] || '' : ''}
                  onChange={(e) => onChangeParam('port', e.target.value)}
                />
              </>
          }
        </Box>
      )
    }

    if (driver === 'SPP_SOLAR_DRIVER_STECAGRID' && params) {
      const stecaParams: SolarPortStecaGridConfigParams = params as SolarPortStecaGridConfigParams

      return (
        <Box>
          {Measuring}

          <Trans>Connection</Trans>

          <Dropdown
            sx={{ mt: 2 }}
            label={this.app.t('Baudrate')}
            items={baudrates}
            selected={`${stecaParams.rs485_baudrate}`}
            onSelect={value => onChangeParam('rs485_baudrate', value === 'AUTO' ? 'AUTO' : parseInt(value))}
          />
          <Dropdown
            sx={{ mt: 2 }}
            label={this.app.t('Mode')}
            items={{
              '8N1': '8N1', '8N2': '8N2', '8E1': '8E1', '8O1': '8O1',
            }}
            selected={stecaParams.rs485_mode}
            onSelect={value => onChangeParam('rs485_mode', value)}
          />
          <Dropdown
            sx={{ mt: 2 }}
            label={this.app.t('Instance')}
            items={{
              0: '0', 1: '1'
            }}
            selected={`${stecaParams.rs485_instance}`}
            onSelect={value => onChangeParam('rs485_instance', parseInt(value))}
          />
          <Dropdown
            sx={{ mt: 2 }}
            label={this.app.t('Auth Level')}
            items={{
              CUSTOMER: 'CUSTOMER',
              SERVICE: 'SERVICE',
              DEVELOPMENT: 'DEVELOPMENT',
              ADMINISTRATOR: 'ADMINISTRATOR',
            }}
            selected={stecaParams.auth_level as string}
            onSelect={value => onChangeParam('auth_level', value)}
          />

        </Box>
      )
    }

    if ((driver === 'SPP_SOLAR_DRIVER_SOLIS' || driver === 'SPP_SOLAR_DRIVER_SOFAR_G3' || driver === 'SPP_SOLAR_DRIVER_SOFAR_G2') && params) {
      const solisParams: SolarPortSolisConfigParams = params as SolarPortSolisConfigParams
      return (
        <Box>
          {Measuring}

          <Trans>Connection</Trans>

          <ListSelector
            items={{
              SOLAR_CONN_TYPE_MODBUS_RTU_RS485_0:
                this.app.t('ConnType-RS485') +
                ' ' + this.app.t('Port {number}').replace('{number}', '0'),
              SOLAR_CONN_TYPE_MODBUS_RTU_RS485_1:
                this.app.t('ConnType-RS485') +
                ' ' + this.app.t('Port {number}').replace('{number}', '1'),
            }}
            icons={[
              Cable,
              Cable,
            ]}
            selected={solisParams.conn_type}
            onSelect={(e) => onChangeParam('conn_type', e)}
          />
        </Box>
      )
    }

    return <Box>
      {Measuring}
    </Box>
  }
}
